<template>
  <main class="homepage container-fluid d-flex flex-column align-items-center">
    <div class="background-overlay"></div> 

    <section class="hero text-white text-center">
      <h1 class="display-3 fw-bold mb-3">🧾 Tax Engine</h1>
      <p class="lead mb-4">
        A powerful tool to calculate <strong>accurate tax rates</strong> on tobacco and vape products across all U.S. states.
      </p>
    </section>

    <section class="features row justify-content-center text-center mt-5 w-100">
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>🌍 Nationwide Coverage</h5>
      <p>Get precise tax calculations for all 51 US states.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>⚡ Define Tax Rules</h5>
      <p>Users can define tax rules based on State and Product Category.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>📦 Product Versatility</h5>
      <p>Supports E-Cigarettes, Disposables, E-liquids, Tobacco Nicotine and more.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>⚙️ Smart Formula Selection</h5>
      <p>Choose from multiple tax formula types like milliliter, LSU factor, per pack, or cost/sales price, and dynamically enable relevant input fields.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>✏️ Editable Rules</h5>
      <p>Update or delete existing tax rules anytime, with complete control over all fields.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>📁 Bulk Import via CSV</h5>
      <p>Upload tax rules in bulk using a CSV file and process calculations at scale.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>📊 Bulk Tax Calculation</h5>
      <p>Upload product data in CSV and calculate taxes for all entries instantly using saved tax rules.</p>
    </div>
  </div>

  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>🏷️ Category Management</h5>
      <p>Add and update categories for full control over your tax rule structure.</p>
    </div>
  </div>
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>➕ Add Products</h5>
      <p>Add new products with full details like name, category, price, and tax info.</p>
    </div>
  </div>
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>✏️ Edit Products</h5>
      <p>Update product information anytime with a dynamic, user-friendly form.</p>
    </div>
  </div>
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>🗑️ Delete Products</h5>
      <p>Securely delete product entries with confirmation modals.</p>
    </div>
  </div>
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>📥 Bulk Product Upload</h5>
      <p>Upload thousands of products via CSV with automatic validation.</p>
    </div>
  </div>
  <div class="col-md-4 mb-4">
    <div class="feature-card">
      <h5>⚡ Lightning Fast</h5>
      <p>Instant results with real-time tax data, ensuring your compliance stays sharp.</p>
    </div>
  </div>
</section>
<section class="how-it-works mt-5 text-white text-center">
  <h2 class="mb-4">📊 How It Works</h2>
  <div class="row justify-content-center gx-5 gy-4">

    <!-- Tax Calculation Steps -->
    <div class="col-md-5 step-flow">
      <h4 class="mb-3">🧾 Tax Calculation</h4>
      <div class="step-wrapper">
        <div class="step-card-arrow">
          <div class="step-number">1</div>
          <p>Go to <router-link to="/taxcalculation" class="text-light">Tax Calculation</router-link></p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">2</div>
          <p>Enter Product Name and Quantity</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">3</div>
          <p>Select State and Category</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">4</div>
          <p>Enter Cost Price, Sales Price</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">4</div>
          <p>Select Distributor</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">5</div>
          <p>System matches tax rule and calculates output</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">6</div>
          <p>If no rule found, message: <em>No tax rules found for selected state and category</em></p>
        </div>
      </div>
    </div>

    <!-- SKU Tax Calculation Steps -->
    <div class="col-md-5 step-flow">
      <h4 class="mb-3">📦 SKU Tax Calculation</h4>
      <div class="step-wrapper">
        <div class="step-card-arrow">
          <div class="step-number">1</div>
          <p>Go to <router-link to="/skutaxcalculation" class="text-light">SKU Tax Calculation</router-link></p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">2</div>
          <p>Enter a valid SKU ID from product list</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">3</div>
          <p>Select a specific state or leave blank</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">4</div>
          <p>System shows tax output for selected state or all states</p>
        </div>
        <div class="step-card-arrow">
          <div class="step-number">5</div>
          <p>If SKU is invalid → message: <em>No product found with SKU</em></p>
        </div>
      </div>
    </div>

  </div>
</section>

    <section class="tax-map mt-5 text-white text-center">
      <h2 class="mb-3">🧭 51-State Tax Intelligence</h2>
      <p class="mb-4">
        Our system keeps track of evolving tax regulations across the U.S. for accurate and up-to-date results.
      </p>

    
      <div class="map-container mb-4" tabindex="0" role="img" aria-label="Interactive map of US states">
        <img
          :src="img1"
          alt="Map of 51 US states"
          class="map-image"
          draggable="false"
        />
       
      </div>

     
    </section>
  </main>
</template>

<script>
import img from '../assets/images/usstate.jpg'
import img1 from '../assets/images/USAMap.png'
export default {
  data() {
    return {
      img,
      img1,
      activeState: null,
     
    }
  }
}
</script>

<style scoped>
.background-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background:
    linear-gradient(135deg, rgba(15, 32, 39, 0.85), rgba(32, 58, 67, 0.85), rgba(44, 83, 100, 0.85)),
    url('../assets/images/usstate.jpg') no-repeat center;
 background-size: cover;
  filter: blur;
  z-index: -1;
  pointer-events: none;
}
.homepage {
  min-height: 100vh;
  width: 100%;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  color: white;
}

.feature-card, .step-card {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(0, 114, 255, 0.25);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  transition: transform 0.3s ease;
  cursor: default;
  height: 100%; 
  min-height: 150px;
  display: flex;
  flex-direction: column;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  width: 100%;
}

.feature-card:hover, .step-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 40px 0 rgba(0, 114, 255, 0.4);
}

@media (max-width: 768px) {
  .cards-container {
    grid-template-columns: 1fr; 
  }
  
  .feature-card, .step-card {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .map-container {
    max-width: 100%;
  }
}

.map-container {
  width: 100%;
  max-width: 80%;
  height: 40%;
  margin: 0 auto;
  position: relative;
  border-radius: 16px;
  overflow: visible;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 114, 255, 0.5);
}
.map-image {
  width: 100%;
  height: auto;
  display: block;
  user-select: none;
  pointer-events: none;
  filter: brightness(0.9);
  border-radius: 16px;
}
.step-flow {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px rgba(0, 114, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.step-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
}

.step-card-arrow {
  background: rgba(255, 255, 255, 0.12);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  position: relative;
  text-align: left;
  padding-left: 3rem;
  box-shadow: 0 6px 20px rgba(0, 114, 255, 0.2);
}

.step-card-arrow::before {
  content: "";
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;

  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 114, 255, 0.8);
}

.step-number {
  position: absolute;
  left: -0.75rem;
  top: -0.75rem;
  background: #0072ff;
  color: white;
  width: 30px;
  height: 30px;
  font-size: 1rem;
  font-weight: bold;
  text-align: center;
  line-height: 30px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 114, 255, 0.6);
}

.step-card-arrow p {
  margin: 0;
  font-size: 0.95rem;
}

.step-card-arrow em {
  font-style: italic;
  color: #ffc107;
}

@media (max-width: 768px) {
  .step-flow {
    margin-bottom: 2rem;
  }
}

</style>